import useApiFetch, {apiEndpoint} from "@tracker/shared/src/Auth/Api";
import {useSnackbar} from 'notistack';
import React, {createContext, useCallback, useContext, useEffect, useState} from "react";
import type {ReactElement, ReactNode} from "react";

type UnsubmittedType = {
    unsubmittedCount : number,
    increaseUnsubmittedCount : () => void,
    decreaseUnsubmittedCount : () => void,
    refreshUnsubmittedCount : () => void,
}

const UnsubmittedContext = createContext<UnsubmittedType>({
    unsubmittedCount: -1,
    /* eslint-disable @typescript-eslint/no-empty-function */
    increaseUnsubmittedCount: () => {},
    decreaseUnsubmittedCount: () => {},
    refreshUnsubmittedCount: () => {},
    /* eslint-enable @typescript-eslint/no-empty-function */
});

export const useUnsubmittedContext = () : UnsubmittedType => {
    const context = useContext(UnsubmittedContext);

    if (Object.keys(context).length === 0) {
        throw new Error('useUnsubmittedContext must be used within a UnsubmittedContextProvider');
    }

    return context;
};

export const UnsubmittedContextProvider = ({children} : {children : ReactNode}) : ReactElement => {
    const [unsubmittedCount, setUnsubmittedCount] = useState<number>(0);
    const apiFetch = useApiFetch();
    const {enqueueSnackbar} = useSnackbar();

    const increaseUnsubmittedCount = () => {
        setUnsubmittedCount(unsubmittedCount+1);
    };

    const decreaseUnsubmittedCount = () => {
        if (unsubmittedCount > 0) {
            setUnsubmittedCount(unsubmittedCount - 1);
        }
    };

    const refreshUnsubmittedCount = useCallback(async () => {
        const url = new URL('/v1/time-entries', apiEndpoint);
        url.searchParams.set('unSubmitted', '1');
        try {
            const response = await apiFetch(url.toString());
            const data = await response.json();
            setUnsubmittedCount(data.length);
        } catch (e) {
            console.error('error fetching unsubmitted', e);
            enqueueSnackbar('There was an error fetching the unsubmitted count');
        }
    }, [apiFetch, enqueueSnackbar]);

    useEffect(() => {
        refreshUnsubmittedCount();
    }, [refreshUnsubmittedCount]);

    return (<UnsubmittedContext.Provider value={{
        unsubmittedCount,
        increaseUnsubmittedCount,
        decreaseUnsubmittedCount,
        refreshUnsubmittedCount,
    }}>
        {children}
    </UnsubmittedContext.Provider>)
}
